import React, { useState, useEffect, useRef } from "react";
import { useAtlas, WORKSPACES } from "../../store/atlasStore";
import { Search, LayoutDashboard, TrendingUp, FlaskConical, Cpu, BarChart3, BookOpen, Network, Server } from "lucide-react";
import axios from "axios";
import { API } from "../atlas/helpers";

const WS_ICONS = {
  overview: LayoutDashboard, markets: TrendingUp, research: FlaskConical,
  decision: Cpu, paper: BarChart3, models: BookOpen, features: Network, system: Server,
};

export function CommandPalette() {
  const { state, dispatch } = useAtlas();
  const [query, setQuery] = useState("");
  const [features, setFeatures] = useState([]);
  const [experiments, setExperiments] = useState([]);
  const [models, setModels] = useState([]);
  const [focusIdx, setFocusIdx] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    if (state.cmdOpen) {
      setQuery("");
      setFocusIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
      // pre-load data
      axios.get(`${API}/registry/features`, { params: { q: "" } })
        .then(r => setFeatures(r.data.slice(0, 200))).catch(() => {});
      axios.get(`${API}/learning/experiments`, { params: { limit: 30 } })
        .then(r => setExperiments(r.data)).catch(() => {});
      axios.get(`${API}/learning/models`, { params: { limit: 30 } })
        .then(r => setModels(r.data)).catch(() => {});
    }
  }, [state.cmdOpen]);

  if (!state.cmdOpen) return null;

  const q = query.toLowerCase();

  const matchedWS = WORKSPACES.filter(w =>
    w.label.toLowerCase().includes(q) || w.id.toLowerCase().includes(q)
  );

  const matchedFeats = q.length >= 2
    ? features.filter(f => f.id.toLowerCase().includes(q) || (f.name || "").toLowerCase().includes(q)).slice(0, 6)
    : [];

  const matchedExp = q.length >= 2
    ? experiments.filter(e => (e.spec?.name || e.experiment_id || "").toLowerCase().includes(q)).slice(0, 4)
    : [];

  const matchedModels = q.length >= 2
    ? models.filter(m => (m.model_id || "").toLowerCase().includes(q) || (m.kind || "").toLowerCase().includes(q)).slice(0, 4)
    : [];

  const allItems = [
    ...matchedWS.map(w => ({ type: "workspace", ...w })),
    ...matchedFeats.map(f => ({ type: "feature", id: f.id, label: f.name || f.id, sub: f.id })),
    ...matchedExp.map(e => ({ type: "experiment", id: e.experiment_id, label: e.spec?.name || e.experiment_id?.slice(0,8), sub: e.spec?.model?.kind })),
    ...matchedModels.map(m => ({ type: "model", id: m.model_id, label: m.model_id?.slice(0,12), sub: m.kind })),
  ];

  const navigate = (item) => {
    if (item.type === "workspace") {
      dispatch({ type: "SET_WORKSPACE", payload: item.id });
    } else if (item.type === "feature") {
      dispatch({ type: "SET_WORKSPACE", payload: "features" });
    } else if (item.type === "experiment" || item.type === "model") {
      dispatch({ type: "SET_WORKSPACE", payload: "research" });
    }
    dispatch({ type: "CLOSE_CMD" });
  };

  const handleKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setFocusIdx(i => Math.min(i+1, allItems.length-1)); }
    if (e.key === "ArrowUp")   { e.preventDefault(); setFocusIdx(i => Math.max(i-1, 0)); }
    if (e.key === "Enter" && allItems[focusIdx]) navigate(allItems[focusIdx]);
    if (e.key === "Escape") dispatch({ type: "CLOSE_CMD" });
  };

  let globalIdx = -1;

  const renderSection = (title, items) => {
    if (!items.length) return null;
    return (
      <div key={title}>
        <div className="cmd-section-label">{title}</div>
        {items.map((item) => {
          globalIdx++;
          const idx = globalIdx;
          const Icon = item.type === "workspace" ? (WS_ICONS[item.id] || Search) : Search;
          return (
            <div
              key={item.id || idx}
              className={`cmd-item${focusIdx === idx ? " focused" : ""}`}
              onMouseEnter={() => setFocusIdx(idx)}
              onClick={() => navigate(item)}
            >
              <Icon className="cmd-item-icon" size={13} />
              <span className="cmd-item-main">{item.label}</span>
              {item.sub && <span className="cmd-item-sub">{item.sub}</span>}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="cmd-overlay" onClick={() => dispatch({ type: "CLOSE_CMD" })}>
      <div className="cmd-box fade-in" onClick={e => e.stopPropagation()}>
        <div className="cmd-input-wrap">
          <Search size={14} style={{ color: "var(--text-tertiary)", flexShrink: 0 }} />
          <input
            ref={inputRef}
            className="cmd-input"
            placeholder="Search workspaces, features, experiments, models…"
            value={query}
            onChange={e => { setQuery(e.target.value); setFocusIdx(0); }}
            onKeyDown={handleKey}
            spellCheck={false}
          />
          {query && (
            <button className="btn-ghost btn-icon" onClick={() => setQuery("")}>✕</button>
          )}
        </div>

        <div className="cmd-results">
          {q.length === 0 && (
            <>
              <div className="cmd-section-label">Workspaces</div>
              {WORKSPACES.map((w, i) => {
                const Icon = WS_ICONS[w.id] || Search;
                return (
                  <div key={w.id} className={`cmd-item${focusIdx === i ? " focused" : ""}`}
                    onMouseEnter={() => setFocusIdx(i)}
                    onClick={() => navigate({ type: "workspace", ...w })}
                  >
                    <Icon className="cmd-item-icon" size={13} />
                    <span className="cmd-item-main">{w.label}</span>
                  </div>
                );
              })}
            </>
          )}
          {q.length > 0 && (
            <>
              {renderSection("Workspaces", matchedWS.map(w => ({ ...w, type: "workspace" })))}
              {renderSection("Features", matchedFeats.map(f => ({ type: "feature", id: f.id, label: f.name || f.id, sub: f.id })))}
              {renderSection("Experiments", matchedExp.map(e => ({ type: "experiment", id: e.experiment_id, label: e.spec?.name || e.experiment_id?.slice(0,8), sub: e.spec?.model?.kind })))}
              {renderSection("Models", matchedModels.map(m => ({ type: "model", id: m.model_id, label: m.model_id?.slice(0,12), sub: m.kind })))}
              {allItems.length === 0 && (
                <div style={{ padding: "20px", textAlign: "center", color: "var(--text-tertiary)", fontSize: 11 }}>
                  No results for "{query}"
                </div>
              )}
            </>
          )}
        </div>

        <div className="cmd-footer">
          <span className="cmd-key">↑↓</span>
          <span style={{ fontSize: 10, color: "var(--text-tertiary)" }}>navigate</span>
          <span className="cmd-key">↵</span>
          <span style={{ fontSize: 10, color: "var(--text-tertiary)" }}>select</span>
          <span className="cmd-key">Esc</span>
          <span style={{ fontSize: 10, color: "var(--text-tertiary)" }}>close</span>
        </div>
      </div>
    </div>
  );
}
